import React, { useState, useEffect } from 'react';
import { Row, Col, Select, Input, InputNumber, Button } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

const { Option } = Select;

// Define valid operators for each field
const FieldOperators = {
  'order_name': ['equal', 'contains', 'not_contains'],
  'order_address': ['contains', 'not_contains'],
  'order_city': ['equal', 'contains', 'in', 'not_in'],
  'order_customer_name': ['equal', 'contains', 'not_contains'],
  'order_scheduled_shipping_date': ['equal', 'within_days'],
  'order_products_total_count': ['equal', 'greater_than_or_equal', 'less_than_or_equal'],
  'order_product_sku_count': ['equal', 'greater_than_or_equal', 'less_than_or_equal'],
  'product_uid': ['equal', 'in', 'not_in', 'contains'],
  'product_name': ['contains', 'not_contains'],
  'order_sales_channel_id': ['in', 'not_in'], 
  'order_shipping_type': ['in', 'not_in'], 
  'same_product': ['exists'], 
  'same_product_and_quantity': ['exists'], 
};

/**
 * @param {Object} props
 * @param {Object} props.rule
 * @param {Function} props.onUpdate
 * @param {Function} props.onRemove
 * @param {Array<Object>} [props.salesChannels]
 * @param {Array<Object>} [props.shippingTypes]
 */
export default function PickingListPresetRule({ rule, onUpdate, onRemove, salesChannels = [], shippingTypes = [] }) {
  const { t } = useTranslation();
  const [field, setField] = useState(rule.field);
  const [comparator, setComparator] = useState(rule.comparator);
  const [values, setValues] = useState(rule.values || []);

  useEffect(() => {
    setField(rule.field);
    setComparator(rule.comparator);
    setValues(rule.values || []);
  }, [rule]);

  const handleFieldChange = (newField) => {
    setField(newField);
    
    const availableOps = FieldOperators[newField] || [];
    let newComparator = '';
    
    if (availableOps.includes('exists')) {
      newComparator = 'exists';
    } else if (availableOps.length > 0) {
      newComparator = availableOps[0];
    }

    setComparator(newComparator);
    const newValues = [];
    setValues(newValues);
    onUpdate({ ...rule, field: newField, comparator: newComparator, values: newValues });
  };

  const handleComparatorChange = (newComparator) => {
    setComparator(newComparator);
    onUpdate({ ...rule, comparator: newComparator });
  };

  const handleValuesChange = (newValues) => {
    setValues(newValues);
    onUpdate({ ...rule, values: newValues });
  };

  const renderValueInput = () => {
    if (comparator === 'exists') return null;

    if (field === 'order_sales_channel_id') {
      return (
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder={t('common.placeholder.please_select')}
          value={values.map(v => isNaN(v) ? v : parseInt(v))}
          onChange={handleValuesChange}
          optionFilterProp="children"
        >
          {salesChannels.map(sc => (
            <Option key={sc.id} value={sc.id}>{sc.name} ({sc.code})</Option>
          ))}
        </Select>
      );
    }

    if (field === 'order_shipping_type') {
      return (
        <Select
          mode="multiple"
          style={{ width: '100%' }}
          placeholder={t('common.placeholder.please_select')}
          value={values.map(v => isNaN(v) ? v : parseInt(v))}
          onChange={handleValuesChange}
          optionFilterProp="children"
        >
          {shippingTypes.map(st => (
            <Option key={st.id} value={st.id}>{t(`shipping_types.${st.code}`, { defaultValue: st.name })}</Option>
          ))}
        </Select>
      );
    }

    if (['in', 'not_in'].includes(comparator)) {
        return <Select
            mode="tags"
            style={{ width: '100%' }}
            placeholder={t('common.placeholder.please_enter')}
            value={values}
            onChange={handleValuesChange}
        />
    }

    if (['equal', 'greater_than_or_equal', 'less_than_or_equal', 'within_days'].includes(comparator)) {
       if (['order_products_total_count', 'order_product_sku_count', 'order_scheduled_shipping_date'].includes(field) || comparator === 'within_days') {
           return <InputNumber 
                style={{ width: '100%' }} 
                value={values[0]} 
                onChange={v => handleValuesChange([v])} 
            />
       }
       return <Input 
            value={values[0]} 
            onChange={e => handleValuesChange([e.target.value])} 
        />
    }

    return <Input 
        value={values[0]} 
        onChange={e => handleValuesChange([e.target.value])} 
    />
  };

  const currentOperators = FieldOperators[field] || [];

  return (
    <Row gutter={8} style={{ marginBottom: 10 }} align="middle">
      <Col span={1}>
        <Button 
            type="text" 
            danger 
            icon={<DeleteOutlined />} 
            onClick={onRemove} 
        />
      </Col>
      <Col span={6}>
        <Select 
            value={field} 
            onChange={handleFieldChange} 
            style={{ width: '100%' }} 
            placeholder={t('common.placeholder.please_select')}
        >
          {Object.keys(FieldOperators).map((key) => (
            <Option key={key} value={key}>{t(`operator.picking_lists.preset_form.rules.fields.${key}`)}</Option>
          ))}
        </Select>
      </Col>
      <Col span={4}>
         {comparator === 'exists' ? (
             <Input disabled value={t('operator.picking_lists.preset_form.rules.operators.exists')} /> 
         ) : (
            <Select 
                value={comparator} 
                onChange={handleComparatorChange} 
                style={{ width: '100%' }} 
                placeholder={t('common.placeholder.please_select')}
            >
            {currentOperators.map(op => (
                <Option key={op} value={op}>{t(`operator.picking_lists.preset_form.rules.operators.${op}`)}</Option>
            ))}
            </Select>
         )}
      </Col>
      <Col span={13}>
        {renderValueInput()}
      </Col>
    </Row>
  );
}
